'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Clapperboard, ImageIcon, Loader2, Save, Sparkles, Trash2, Users, Video } from 'lucide-react';
import TopHeader from '@/components/TopHeader';
import BottomNav from '@/components/BottomNav';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAdminRuntimeConfig } from '@/hooks/use-admin-runtime-config';
import { useSession } from '@/hooks/useSessionHook';
import supabase from '@/supabase';
import { getBotAvatarSrc } from '@/components/bots/getBotAvatarSrc';
import type { Bot, BotMediaReference, BotPersona, CompanionAccessTier } from '@/types/bot';
import { resolveMediaLocationUrl } from '@/lib/admin-runtime-config';
import { generateCorrelationId, getRepoHeaders } from '@/lib/runtime-monitoring';

type CompanionProfile = {
  id: string;
  username: string | null;
  display_name: string | null;
  avatar_url: string | null;
};

type CompanionListItem = {
  id: string;
  updated_at: string;
  profile: CompanionProfile | null;
};

type BotStoryListItem = {
  id: string;
  media_type: 'image' | 'video';
  caption: string | null;
  created_at: string;
  expires_at: string;
  status: string;
  media_url: string | null;
  thumbnail_url: string | null;
};

type GatewayImageResponse = {
  success?: boolean;
  data?: {
    images?: Array<{
      data?: string;
      image_url?: string;
    }>;
  };
  error?: {
    message?: string;
  };
};

type GatewayVideoResponse = {
  success?: boolean;
  data?: {
    status?: 'pending' | 'processing' | 'completed' | 'failed';
    video_url?: string;
    thumbnail_url?: string;
  };
  error?: {
    message?: string;
  };
};

const COMPANIONS_PAGE_SIZE = 10;
const BOT_ASSET_MAX_IMAGE_SIZE = 10 * 1024 * 1024;
const BOT_ASSET_MAX_VIDEO_SIZE = 50 * 1024 * 1024;
const BOT_ASSET_FOLDER_OPTIONS = [
  { value: 'general', label: 'General' },
  { value: 'portraits', label: 'Portraits' },
  { value: 'selfies', label: 'Selfies' },
  { value: 'scenes', label: 'Scenes' },
  { value: 'videos', label: 'Videos' },
] as const;

const COMPANION_ACCESS_OPTIONS: Array<{ value: CompanionAccessTier; label: string }> = [
  { value: 'free', label: 'Free' },
  { value: 'preferred', label: 'Preferred' },
  { value: 'private', label: 'Private' },
];

function companionAccessLabel(tier: CompanionAccessTier): string {
  return COMPANION_ACCESS_OPTIONS.find((option) => option.value === tier)?.label ?? 'Free';
}

function splitCsv(value: string): string[] {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function initialsFor(name: string | null | undefined): string {
  if (!name) return '?';
  return name
    .split(' ')
    .map((part) => part.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        reject(new Error('Failed to read file.'));
        return;
      }
      resolve(reader.result);
    };
    reader.onerror = () => reject(new Error('Failed to read file.'));
    reader.readAsDataURL(file);
  });
}

async function createVideoThumbnail(file: File): Promise<File> {
  const objectUrl = URL.createObjectURL(file);

  try {
    const video = document.createElement('video');
    video.src = objectUrl;
    video.muted = true;
    video.playsInline = true;
    video.preload = 'metadata';

    await new Promise<void>((resolve, reject) => {
      video.onloadeddata = () => resolve();
      video.onerror = () => reject(new Error('Unable to read uploaded video.'));
    });

    video.currentTime = Math.min(0.1, video.duration || 0.1);

    await new Promise<void>((resolve, reject) => {
      video.onseeked = () => resolve();
      video.onerror = () => reject(new Error('Unable to seek uploaded video.'));
    });

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');

    if (!context) {
      throw new Error('Unable to create preview thumbnail.');
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((thumbnailBlob) => {
        if (!thumbnailBlob) {
          reject(new Error('Unable to generate preview thumbnail.'));
          return;
        }
        resolve(thumbnailBlob);
      }, 'image/jpeg', 0.85);
    });

    return new File([blob], 'bot-asset-thumbnail.jpg', { type: 'image/jpeg' });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export default function BotAdminPage() {
  const { botId } = useParams<{ botId: string }>();
  const navigate = useNavigate();
  const { profile } = useSession();
  const { config: adminRuntimeConfig } = useAdminRuntimeConfig();
  const isAdmin = profile?.app_role === 'admin';

  const [bot, setBot] = useState<Bot | null>(null);
  const [formState, setFormState] = useState({
    name: '',
    avatar_url: '',
    description: '',
    system_prompt: '',
    tone: '',
    personality_traits: '',
    knowledge_base: '',
    greeting_messages: '',
    boundaries: '',
  });
  const [mediaReferences, setMediaReferences] = useState<BotMediaReference[]>([]);
  const [companions, setCompanions] = useState<CompanionListItem[]>([]);
  const [stories, setStories] = useState<BotStoryListItem[]>([]);
  const [companionsTotalCount, setCompanionsTotalCount] = useState(0);
  const [companionsPage, setCompanionsPage] = useState(1);
  const [assetType, setAssetType] = useState<'image' | 'video'>('image');
  const [assetPrompt, setAssetPrompt] = useState('');
  const [assetUploadFolder, setAssetUploadFolder] = useState<string>(BOT_ASSET_FOLDER_OPTIONS[0].value);
  const [assetAccessTier, setAssetAccessTier] = useState<CompanionAccessTier>('free');
  const [assetUnlockCost, setAssetUnlockCost] = useState(0);
  const [selectedStoryAssetId, setSelectedStoryAssetId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingAsset, setIsGeneratingAsset] = useState(false);
  const [isUploadingAsset, setIsUploadingAsset] = useState(false);
  const [isGeneratingStory, setIsGeneratingStory] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const fetchStories = useCallback(async () => {
    if (!botId) {
      return [];
    }

    const { data, error } = await supabase.functions.invoke('admin-manage-bot-stories', {
      body: {
        action: 'list_stories',
        bot_id: botId,
      },
    });

    if (error) throw error;
    return (data?.stories || []) as BotStoryListItem[];
  }, [botId]);

  const fetchCompanionsPage = useCallback(async (page: number) => {
    if (!botId) {
      return {
        companions: [] as CompanionListItem[],
        totalCount: 0,
      };
    }

    const from = (page - 1) * COMPANIONS_PAGE_SIZE;
    const to = from + COMPANIONS_PAGE_SIZE - 1;

    const { data: conversationData, error: companionError, count } = await supabase
      .from('bot_conversations')
      .select('id, user_id, updated_at', { count: 'exact' })
      .eq('bot_id', botId)
      .order('updated_at', { ascending: false })
      .range(from, to);

    if (companionError) throw companionError;

    const userIds = Array.from(new Set((conversationData || []).map((conversation) => conversation.user_id)));
    let profileMap = new Map<string, CompanionProfile>();

    if (userIds.length > 0) {
      const { data: profileRows, error: profileError } = await supabase
        .from('profiles')
        .select('id, username, display_name, avatar_url')
        .in('id', userIds);

      if (profileError) throw profileError;

      profileMap = new Map((profileRows || []).map((row) => [row.id, row as CompanionProfile]));
    }

    return {
      companions: (conversationData || []).map((conversation) => ({
        id: conversation.id,
        updated_at: conversation.updated_at,
        profile: profileMap.get(conversation.user_id) || null,
      })),
      totalCount: count || 0,
    };
  }, [botId]);

  useEffect(() => {
    if (!botId || !isAdmin) {
      setIsLoading(false);
      return;
    }

    const fetchBotAdminData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const [botResult, mediaResult, companionResult, storyResult] = await Promise.allSettled([
          supabase.from('bots').select('*').eq('id', botId).single(),
          supabase.from('bot_media_references').select('*').eq('bot_id', botId).order('created_at', { ascending: false }),
          fetchCompanionsPage(1),
          fetchStories(),
        ]);

        if (botResult.status !== 'fulfilled') {
          throw botResult.reason;
        }

        if (mediaResult.status !== 'fulfilled') {
          throw mediaResult.reason;
        }

        if (companionResult.status !== 'fulfilled') {
          throw companionResult.reason;
        }

        const { data: botData, error: botError } = botResult.value;
        const { data: mediaData, error: mediaError } = mediaResult.value;
        const resolvedCompanionResult = companionResult.value;
        const resolvedStoryResult = storyResult.status === 'fulfilled' ? storyResult.value : [];

        if (botError) throw botError;
        if (mediaError) throw mediaError;

        const typedBot = botData as Bot;
        const persona = typedBot.persona as BotPersona;

        setBot(typedBot);
        setFormState({
          name: typedBot.name,
          avatar_url: typedBot.avatar_url || '',
          description: persona.description || '',
          system_prompt: persona.system_prompt || '',
          tone: persona.tone || '',
          personality_traits: (persona.personality_traits || []).join(', '),
          knowledge_base: (persona.knowledge_base || []).join(', '),
          greeting_messages: (persona.greeting_messages || []).join(', '),
          boundaries: typedBot.boundaries || '',
        });
        setMediaReferences((mediaData || []) as BotMediaReference[]);
        setStories(resolvedStoryResult);
        setCompanions(resolvedCompanionResult.companions);
        setCompanionsTotalCount(resolvedCompanionResult.totalCount);
        setCompanionsPage(1);

        if (storyResult.status !== 'fulfilled') {
          console.error('Failed to load stories:', storyResult.reason);
          setStatusMessage('Stories are unavailable until the admin story function is deployed.');
        }
      } catch (fetchError) {
        console.error('Failed to load admin bot data:', fetchError);
        setError('Failed to load bot management data.');
      } finally {
        setIsLoading(false);
      }
    };

    void fetchBotAdminData();
  }, [botId, fetchCompanionsPage, fetchStories, isAdmin]);

  useEffect(() => {
    if (!selectedStoryAssetId && mediaReferences.length > 0) {
      setSelectedStoryAssetId(mediaReferences[0].id);
    }
  }, [mediaReferences, selectedStoryAssetId]);

  useEffect(() => {
    if (!botId || !isAdmin || isLoading) {
      return;
    }

    const loadCompanionsPage = async () => {
      try {
        const result = await fetchCompanionsPage(companionsPage);
        setCompanions(result.companions);
        setCompanionsTotalCount(result.totalCount);
      } catch (fetchError) {
        console.error('Failed to load companion page:', fetchError);
        setError('Failed to load human companions.');
      }
    };

    void loadCompanionsPage();
  }, [botId, companionsPage, fetchCompanionsPage, isAdmin, isLoading]);

  const avatarPreview = useMemo(() => {
    if (!bot) return formState.avatar_url || undefined;
    return (
      formState.avatar_url ||
      getBotAvatarSrc({
        id: bot.id,
        name: formState.name || bot.name,
        avatar_url: bot.avatar_url,
      }) ||
      undefined
    );
  }, [bot, formState.avatar_url, formState.name]);

  const handlePersonaSave = async () => {
    if (!botId) return;

    setIsSaving(true);
    setError(null);
    setStatusMessage(null);

    try {
      const nextPersona: BotPersona = {
        description: formState.description.trim(),
        system_prompt: formState.system_prompt.trim(),
        tone: formState.tone.trim(),
        personality_traits: splitCsv(formState.personality_traits),
        knowledge_base: splitCsv(formState.knowledge_base),
        greeting_messages: splitCsv(formState.greeting_messages),
      };

      const { data, error: updateError } = await supabase
        .from('bots')
        .update({
          name: formState.name.trim(),
          avatar_url: formState.avatar_url.trim() || null,
          persona: nextPersona,
          boundaries: formState.boundaries.trim() || null,
        })
        .eq('id', botId)
        .select('*')
        .single();

      if (updateError) throw updateError;

      setBot(data as Bot);
      setStatusMessage('Bot profile updated.');
    } catch (saveError) {
      console.error('Failed to save bot persona:', saveError);
      setError('Failed to save bot profile.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAssetCreate = async () => {
    if (!botId) return;

    const trimmedPrompt = assetPrompt.trim();
    if (!trimmedPrompt) {
      setError('Enter an image prompt before generating an asset.');
      return;
    }

    setIsGeneratingAsset(true);
    setError(null);
    setStatusMessage(null);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;

      if (!accessToken) {
        throw new Error('Not authenticated');
      }

      const generationCorrelationId = generateCorrelationId();
      const generationResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-gateway`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          ...getRepoHeaders(generationCorrelationId),
        },
        body: JSON.stringify({
          bot_id: botId,
          request_type: assetType,
          provider_options: {
            chat_generation_url: adminRuntimeConfig.chat_generation_url,
            media_generation_url: adminRuntimeConfig.media_generation_url,
            media_location_url: adminRuntimeConfig.media_location_url,
          },
          prompt: trimmedPrompt,
          ...(assetType === 'image'
            ? {
                image_options: {
                  n: 1,
                },
              }
            : {
                video_options: {
                  duration: 5,
                  aspect_ratio: '9:16',
                },
              }),
        }),
      });

      if (assetType === 'image') {
        const imagePayload = (await generationResponse.json().catch(() => ({}))) as GatewayImageResponse;

        if (!generationResponse.ok) {
          throw new Error(imagePayload.error?.message || 'Image generation failed.');
        }

        const generatedImage = imagePayload.data?.images?.[0]?.data;
        if (!generatedImage) {
          throw new Error('Image generation returned no asset data.');
        }

        const { data, error: invokeError } = await supabase.functions.invoke('admin-create-generated-bot-asset', {
          body: {
            bot_id: botId,
            prompt: trimmedPrompt,
            media_type: 'image',
            image_base64: generatedImage,
            access_tier_required: assetAccessTier,
            credits_required: assetUnlockCost,
          },
        });

        if (invokeError) throw invokeError;
        if (!data?.asset) {
          throw new Error('Generated image could not be assigned to the bot.');
        }

        setMediaReferences((prev) => [data.asset as BotMediaReference, ...prev]);
        setStatusMessage('Image generated and added to assets.');
      } else {
        const videoPayload = (await generationResponse.json().catch(() => ({}))) as GatewayVideoResponse;

        if (!generationResponse.ok) {
          throw new Error(videoPayload.error?.message || 'Video generation failed.');
        }

        if (videoPayload.data?.status && videoPayload.data.status !== 'completed') {
          throw new Error('Video generation started, but the provider returned an async job instead of a finished video. Polling is not wired up yet.');
        }

        if (!videoPayload.data?.video_url) {
          throw new Error('Video generation returned no video URL.');
        }

        const { data, error: invokeError } = await supabase.functions.invoke('admin-create-generated-bot-asset', {
          body: {
            bot_id: botId,
            prompt: trimmedPrompt,
            media_type: 'video',
            source_url: resolveMediaLocationUrl(videoPayload.data.video_url, adminRuntimeConfig.media_location_url),
            preview_url: resolveMediaLocationUrl(videoPayload.data.thumbnail_url || null, adminRuntimeConfig.media_location_url),
            access_tier_required: assetAccessTier,
            credits_required: assetUnlockCost,
          },
        });

        if (invokeError) throw invokeError;
        if (!data?.asset) {
          throw new Error('Generated video could not be assigned to the bot.');
        }

        setMediaReferences((prev) => [data.asset as BotMediaReference, ...prev]);
        setStatusMessage('Video generated and added to assets.');
      }

      setAssetPrompt('');
    } catch (insertErr) {
      console.error('Failed to create generated bot asset:', insertErr);
      setError(insertErr instanceof Error ? insertErr.message : 'Failed to generate asset.');
    } finally {
      setIsGeneratingAsset(false);
    }
  };

  const handleAssetDelete = async (assetId: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('bot_media_references')
        .delete()
        .eq('id', assetId);

      if (deleteError) throw deleteError;

      setMediaReferences((prev) => prev.filter((asset) => asset.id !== assetId));
      setStatusMessage('Asset removed.');
    } catch (deleteErr) {
      console.error('Failed to delete asset:', deleteErr);
      setError('Failed to delete asset.');
    }
  };

  const handleAssetUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!botId) return;

    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    const inferredAssetType = file.type.startsWith('video/') ? 'video' : file.type.startsWith('image/') ? 'image' : null;
    if (!inferredAssetType) {
      setError('Only image and video uploads are supported.');
      return;
    }

    if (inferredAssetType === 'image' && file.size > BOT_ASSET_MAX_IMAGE_SIZE) {
      setError('Image assets must be 10MB or smaller.');
      return;
    }

    if (inferredAssetType === 'video' && file.size > BOT_ASSET_MAX_VIDEO_SIZE) {
      setError('Video assets must be 50MB or smaller.');
      return;
    }

    setIsUploadingAsset(true);
    setError(null);
    setStatusMessage(null);

    try {
      const mediaBase64 = await fileToDataUrl(file);
      const previewFile = inferredAssetType === 'video' ? await createVideoThumbnail(file) : file;
      const previewBase64 = await fileToDataUrl(previewFile);

      const { data, error: uploadError } = await supabase.functions.invoke('admin-upload-bot-asset', {
        body: {
          bot_id: botId,
          media_type: inferredAssetType,
          asset_folder: assetUploadFolder,
          access_tier_required: assetAccessTier,
          credits_required: assetUnlockCost,
          media_base64: mediaBase64,
          media_content_type: file.type,
          preview_base64: previewBase64,
          preview_content_type: previewFile.type,
        },
      });

      if (uploadError) throw uploadError;
      if (!data?.asset) {
        throw new Error('Uploaded asset could not be assigned to the bot.');
      }

      setMediaReferences((prev) => [data.asset as BotMediaReference, ...prev]);
      setStatusMessage('Asset uploaded to companion media.');
    } catch (uploadErr) {
      console.error('Failed to upload asset:', uploadErr);
      setError(uploadErr instanceof Error ? uploadErr.message : 'Failed to upload asset.');
    } finally {
      setIsUploadingAsset(false);
    }
  };

  const handleStoryGenerate = async () => {
    if (!botId || !selectedStoryAssetId) {
      setError('Select an asset to create a story.');
      return;
    }

    const selectedAsset = mediaReferences.find((asset) => asset.id === selectedStoryAssetId);
    if (!selectedAsset) {
      setError('Selected asset was not found.');
      return;
    }

    setIsGeneratingStory(true);
    setError(null);
    setStatusMessage(null);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;

      if (!accessToken) {
        throw new Error('Not authenticated');
      }

      const storyPrompt = [
        'Write one short social story caption for this bot asset.',
        'Keep it under 18 words.',
        'No hashtags. No quotation marks. No emojis unless essential.',
        `Asset title: ${selectedAsset.title || 'Untitled asset'}.`,
        `Asset type: ${selectedAsset.type}.`,
        bot ? `Bot name: ${bot.name}.` : null,
        formState.description ? `Bot description: ${formState.description}.` : null,
        formState.tone ? `Tone: ${formState.tone}.` : null,
      ].filter(Boolean).join(' ');

      const promptGenerationCorrelationId = generateCorrelationId();
      const generationResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-gateway`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          ...getRepoHeaders(promptGenerationCorrelationId),
        },
        body: JSON.stringify({
          bot_id: botId,
          request_type: 'chat',
          provider_options: {
            chat_generation_url: adminRuntimeConfig.chat_generation_url,
            media_generation_url: adminRuntimeConfig.media_generation_url,
            media_location_url: adminRuntimeConfig.media_location_url,
          },
          messages: [
            {
              role: 'user',
              content: storyPrompt,
            },
          ],
          chat_options: {
            max_tokens: 60,
            temperature: 0.9,
          },
        }),
      });

      const captionPayload = await generationResponse.json().catch(() => ({} as Record<string, unknown>));
      if (!generationResponse.ok) {
        throw new Error(
          (captionPayload as { error?: { message?: string } }).error?.message || 'Failed to generate story caption.'
        );
      }

      const generatedCaption =
        (captionPayload as { data?: { content?: string; choices?: Array<{ message?: { content?: string } }> } }).data?.content ||
        (captionPayload as { data?: { choices?: Array<{ message?: { content?: string } }> } }).data?.choices?.[0]?.message?.content ||
        '';

      const caption = generatedCaption.trim().replace(/^["']|["']$/g, '');
      if (!caption) {
        throw new Error('Caption generation returned an empty response.');
      }

      const { data, error: storyError } = await supabase.functions.invoke('admin-manage-bot-stories', {
        body: {
          action: 'create_story',
          bot_id: botId,
          asset_id: selectedStoryAssetId,
          caption,
        },
      });

      if (storyError) throw storyError;
      if (!data?.story) {
        throw new Error('Story creation failed.');
      }

      setStories((prev) => [data.story as BotStoryListItem, ...prev]);
      setStatusMessage('Story generated from selected asset.');
    } catch (storyErr) {
      console.error('Failed to generate story:', storyErr);
      setError(storyErr instanceof Error ? storyErr.message : 'Failed to generate story.');
    } finally {
      setIsGeneratingStory(false);
    }
  };

  const handleStoryDelete = async (storyId: string) => {
    if (!botId) return;

    try {
      const { error: deleteError } = await supabase.functions.invoke('admin-manage-bot-stories', {
        body: {
          action: 'delete_story',
          bot_id: botId,
          story_id: storyId,
        },
      });

      if (deleteError) throw deleteError;

      setStories((prev) => prev.filter((story) => story.id !== storyId));
      setStatusMessage('Story removed.');
    } catch (deleteErr) {
      console.error('Failed to delete story:', deleteErr);
      setError('Failed to delete story.');
    }
  };

  const totalCompanionPages = Math.max(1, Math.ceil(companionsTotalCount / COMPANIONS_PAGE_SIZE));

  if (!isAdmin) {
    return (
      <div className="jr-app-shell">
        <TopHeader />
        <main className="flex-1 p-6">
          <Card>
            <CardHeader>
              <CardTitle>Admin Access Required</CardTitle>
              <CardDescription>This bot management screen is only available to admin accounts.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate('/bots')}>Back to Bots</Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="jr-app-shell">
        <TopHeader />
        <main className="flex flex-1 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-[color:var(--jr-accent)]" />
        </main>
      </div>
    );
  }

  return (
    <div className="jr-app-shell">
      <TopHeader />

      <main className="flex-1 overflow-y-auto pb-20">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/bots')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <p className="jr-kicker">Admin Bot Studio</p>
                <h1 className="text-2xl font-semibold">{formState.name || bot?.name || 'Bot'}</h1>
              </div>
            </div>

            {botId && (
              <Button asChild variant="outline">
                <Link to={`/bot/${botId}`}>Open Chat View</Link>
              </Button>
            )}
          </div>

          {error && <div className="jr-status-error">{error}</div>}
          {statusMessage && <div className="jr-status-success">{statusMessage}</div>}

          <Card>
            <CardHeader>
              <CardTitle>Identity And Personality</CardTitle>
              <CardDescription>Adjust the bot’s voice, positioning, and public-facing avatar.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-[120px_1fr] md:items-start">
                <div className="flex flex-col items-center gap-3">
                  <Avatar className="h-24 w-24 border border-border">
                    <AvatarImage src={avatarPreview} alt={formState.name || bot?.name || 'Bot'} />
                    <AvatarFallback>{initialsFor(formState.name || bot?.name)}</AvatarFallback>
                  </Avatar>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="bot-name">Name</Label>
                    <Input id="bot-name" value={formState.name} onChange={(event) => setFormState((prev) => ({ ...prev, name: event.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bot-avatar">Avatar URL</Label>
                    <Input id="bot-avatar" value={formState.avatar_url} onChange={(event) => setFormState((prev) => ({ ...prev, avatar_url: event.target.value }))} placeholder="https://..." />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="bot-description">Description</Label>
                    <Textarea id="bot-description" value={formState.description} onChange={(event) => setFormState((prev) => ({ ...prev, description: event.target.value }))} />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="bot-system-prompt">System Prompt</Label>
                    <Textarea id="bot-system-prompt" className="min-h-40" value={formState.system_prompt} onChange={(event) => setFormState((prev) => ({ ...prev, system_prompt: event.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bot-tone">Tone</Label>
                    <Input id="bot-tone" value={formState.tone} onChange={(event) => setFormState((prev) => ({ ...prev, tone: event.target.value }))} placeholder="warm, flirty, playful" />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="bot-traits">Personality Traits</Label>
                    <Input id="bot-traits" value={formState.personality_traits} onChange={(event) => setFormState((prev) => ({ ...prev, personality_traits: event.target.value }))} placeholder="comma, separated, values" />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="bot-knowledge">Knowledge Areas</Label>
                    <Input id="bot-knowledge" value={formState.knowledge_base} onChange={(event) => setFormState((prev) => ({ ...prev, knowledge_base: event.target.value }))} placeholder="comma, separated, values" />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="bot-greetings">Greeting Messages</Label>
                    <Textarea id="bot-greetings" value={formState.greeting_messages} onChange={(event) => setFormState((prev) => ({ ...prev, greeting_messages: event.target.value }))} placeholder="comma-separated greetings" />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="bot-boundaries">Boundaries</Label>
                    <Textarea id="bot-boundaries" value={formState.boundaries} onChange={(event) => setFormState((prev) => ({ ...prev, boundaries: event.target.value }))} />
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button onClick={() => void handlePersonaSave()} disabled={isSaving}>
                  {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  Save Bot
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Available Assets</CardTitle>
              <CardDescription>Create media for this bot, assign companion access, and manage what is ready for stories.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="rounded-lg border border-dashed p-4">
                <div className="grid gap-4 md:grid-cols-[1fr_180px]">
                  <div className="space-y-2">
                    <Label htmlFor="asset-access-tier">Visible To</Label>
                    <select
                      id="asset-access-tier"
                      value={assetAccessTier}
                      onChange={(event) => setAssetAccessTier(event.target.value as CompanionAccessTier)}
                      className="border-input focus-visible:border-ring focus-visible:ring-ring/50 h-9 w-full rounded-md border bg-transparent px-3 text-sm outline-none focus-visible:ring-[3px]"
                    >
                      {COMPANION_ACCESS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-[color:var(--jr-text-muted)]">
                      Free users see free media. Preferred and private media stay blurred unless the user has that companion membership tier or unlocks it with tokens.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="asset-unlock-cost">Token Cost</Label>
                    <Input
                      id="asset-unlock-cost"
                      type="number"
                      min={0}
                      step={1}
                      value={assetUnlockCost}
                      onChange={(event) => setAssetUnlockCost(Math.max(0, Number(event.target.value) || 0))}
                    />
                    <p className="text-xs text-[color:var(--jr-text-muted)]">
                      Set to `0` for membership-only access with no pay-per-view unlock.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-dashed p-4">
                <div className="grid gap-4 md:grid-cols-[180px_1fr_auto] md:items-end">
                  <div className="space-y-2">
                    <Label htmlFor="asset-type">Asset Type</Label>
                    <select
                      id="asset-type"
                      value={assetType}
                      onChange={(event) => setAssetType(event.target.value as 'image' | 'video')}
                      className="border-input focus-visible:border-ring focus-visible:ring-ring/50 h-9 w-full rounded-md border bg-transparent px-3 text-sm outline-none focus-visible:ring-[3px]"
                    >
                      <option value="image">Image</option>
                      <option value="video">Video</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="asset-prompt">{assetType === 'image' ? 'Image Prompt' : 'Video Prompt'}</Label>
                    <Textarea
                      id="asset-prompt"
                      value={assetPrompt}
                      onChange={(event) => setAssetPrompt(event.target.value)}
                      placeholder={assetType === 'image' ? 'Describe the image to generate for this bot...' : 'Describe the video to generate for this bot...'}
                    />
                    <p className="text-xs text-[color:var(--jr-text-muted)]">
                      The title, preview URL, and media URL are filled automatically after Lightspeed returns the asset and it is stored in `bot-media`.
                    </p>
                  </div>
                  <Button onClick={() => void handleAssetCreate()} disabled={isGeneratingAsset || !assetPrompt.trim()}>
                    {isGeneratingAsset ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                    {assetType === 'image' ? 'Generate Image' : 'Generate Video'}
                  </Button>
                </div>
              </div>

              <div className="rounded-lg border border-dashed p-4">
                <div className="grid gap-4 md:grid-cols-[1fr_auto] md:items-end">
                  <div className="space-y-2">
                    <Label htmlFor="asset-upload-folder">Upload to Companion Media</Label>
                    <select
                      id="asset-upload-folder"
                      value={assetUploadFolder}
                      onChange={(event) => setAssetUploadFolder(event.target.value)}
                      className="border-input focus-visible:border-ring focus-visible:ring-ring/50 h-9 w-full rounded-md border bg-transparent px-3 text-sm outline-none focus-visible:ring-[3px]"
                    >
                      {BOT_ASSET_FOLDER_OPTIONS.map((folder) => (
                        <option key={folder.value} value={folder.value}>
                          {folder.label}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-[color:var(--jr-text-muted)]">
                      Upload an image or video directly into this bot&apos;s companion media library and place it in the selected folder.
                    </p>
                  </div>
                  <div>
                    <Label
                      htmlFor="asset-upload-input"
                      className="jr-action-primary inline-flex h-9 cursor-pointer items-center justify-center px-4 py-2 text-sm"
                    >
                      {isUploadingAsset ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                      Upload Asset
                    </Label>
                    <input
                      id="asset-upload-input"
                      type="file"
                      accept="image/*,video/mp4,video/quicktime,video/webm"
                      className="hidden"
                      disabled={isUploadingAsset}
                      onChange={(event) => void handleAssetUpload(event)}
                    />
                  </div>
                </div>
              </div>

              <Separator />

              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {mediaReferences.length === 0 && <p className="text-sm text-[color:var(--jr-text-muted)]">No media references defined yet.</p>}
                {mediaReferences.map((asset) => (
                  <Card key={asset.id} className="gap-4 py-0">
                    <CardHeader className="px-4 pt-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <CardTitle className="text-base">{asset.title || 'Untitled asset'}</CardTitle>
                          <CardDescription className="mt-1 flex items-center gap-2">
                            {asset.type === 'video' ? <Video className="h-3.5 w-3.5" /> : <ImageIcon className="h-3.5 w-3.5" />}
                            {asset.type}
                          </CardDescription>
                        </div>
                        <div className="flex gap-2">
                          <Badge variant={asset.access_tier_required === 'free' ? 'secondary' : 'default'}>
                            {companionAccessLabel(asset.access_tier_required)}
                          </Badge>
                          <Badge variant="outline">
                            {asset.credits_required > 0 ? `${asset.credits_required} tokens` : 'Included'}
                          </Badge>
                          <Button variant="ghost" size="icon" onClick={() => void handleAssetDelete(asset.id)}>
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3 px-4 pb-4">
                      <div className="aspect-[4/3] overflow-hidden rounded-lg border bg-muted">
                        {asset.type === 'video' ? (
                          <video src={asset.preview_url} className="h-full w-full object-cover" muted playsInline />
                        ) : (
                          <img src={asset.preview_url} alt={asset.title} className="h-full w-full object-cover" />
                        )}
                      </div>
                      <div className="space-y-1 text-xs text-[color:var(--jr-text-muted)]">
                        <p className="truncate">Preview: {asset.preview_url}</p>
                        <p className="truncate">Media: {asset.media_url}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clapperboard className="h-4 w-4" />
                Stories
              </CardTitle>
              <CardDescription>Select an existing asset and generate a caption to create an available story for this bot.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="rounded-lg border border-dashed p-4">
                <div className="grid gap-4 md:grid-cols-[1fr_auto] md:items-end">
                  <div className="space-y-2">
                    <Label htmlFor="story-asset">Source Asset</Label>
                    <select
                      id="story-asset"
                      value={selectedStoryAssetId}
                      onChange={(event) => setSelectedStoryAssetId(event.target.value)}
                      className="border-input focus-visible:border-ring focus-visible:ring-ring/50 h-9 w-full rounded-md border bg-transparent px-3 text-sm outline-none focus-visible:ring-[3px]"
                    >
                      {mediaReferences.length === 0 && <option value="">No assets available</option>}
                      {mediaReferences.map((asset) => (
                        <option key={asset.id} value={asset.id}>
                          {asset.title || 'Untitled asset'} · {asset.type}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-[color:var(--jr-text-muted)]">
                      The caption is generated by AI from the selected asset and the bot persona, then stored as a bot story.
                    </p>
                  </div>
                  <Button onClick={() => void handleStoryGenerate()} disabled={isGeneratingStory || !selectedStoryAssetId}>
                    {isGeneratingStory ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                    Generate Story
                  </Button>
                </div>
              </div>

              <Separator />

              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {stories.length === 0 && <p className="text-sm text-[color:var(--jr-text-muted)]">No stories defined yet.</p>}
                {stories.map((story) => (
                  <Card key={story.id} className="gap-4 py-0">
                    <CardHeader className="px-4 pt-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <CardTitle className="text-base">{story.caption || 'Untitled story'}</CardTitle>
                          <CardDescription className="mt-1 flex items-center gap-2">
                            {story.media_type === 'video' ? <Video className="h-3.5 w-3.5" /> : <ImageIcon className="h-3.5 w-3.5" />}
                            {story.media_type}
                          </CardDescription>
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => void handleStoryDelete(story.id)}>
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3 px-4 pb-4">
                      <div className="aspect-[4/3] overflow-hidden rounded-lg border bg-muted">
                        {story.media_type === 'video' ? (
                          <video src={story.thumbnail_url || undefined} className="h-full w-full object-cover" muted playsInline />
                        ) : (
                          <img src={story.thumbnail_url || story.media_url || undefined} alt={story.caption || 'Story preview'} className="h-full w-full object-cover" />
                        )}
                      </div>
                      <div className="space-y-1 text-xs text-[color:var(--jr-text-muted)]">
                        <p>Created: {new Date(story.created_at).toLocaleString()}</p>
                        <p>Expires: {new Date(story.expires_at).toLocaleString()}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Human Companions
              </CardTitle>
              <CardDescription>Recent users with active or historical conversations with this bot.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {companions.length === 0 && <p className="text-sm text-[color:var(--jr-text-muted)]">No companions yet.</p>}
              {companions.map((companion) => {
                const name = companion.profile?.display_name || companion.profile?.username || 'Unknown user';

                return (
                  <div key={companion.id} className="flex items-center gap-3 rounded-lg border p-3">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={companion.profile?.avatar_url || undefined} alt={name} />
                      <AvatarFallback>{initialsFor(name)}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{name}</p>
                      <p className="text-xs text-[color:var(--jr-text-muted)]">Last active: {new Date(companion.updated_at).toLocaleString()}</p>
                    </div>
                  </div>
                );
              })}

              <div className="flex items-center justify-between gap-3 border-t pt-4">
                <p className="text-xs text-[color:var(--jr-text-muted)]">
                  Page {companionsPage} of {totalCompanionPages} · {companionsTotalCount} total companions
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={companionsPage <= 1} onClick={() => setCompanionsPage((prev) => Math.max(1, prev - 1))}>
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={companionsPage >= totalCompanionPages}
                    onClick={() => setCompanionsPage((prev) => Math.min(totalCompanionPages, prev + 1))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      <footer className="fixed bottom-0 left-0 right-0 border-t bg-white p-2 shadow-lg">
        <BottomNav />
      </footer>
    </div>
  );
}
